import { useCallback, useEffect, useRef, useState } from 'react';
import { getBankerReconVolumeBonusPreview, getVolumeBonusBackendStatus, type VolumeBonusReconPreview } from '@/services/loVolumeBonusService';
import { RECAPTURE_CHANGED_EVENT } from '@/services/recaptureService';

export function useVolumeBonusPreview(reconId: string | null, recipientId: string | null, purchaseDate: string | null) {
  const identity = JSON.stringify([reconId, recipientId, purchaseDate]);
  const [state, setState] = useState<{ identity: string; preview: VolumeBonusReconPreview | null; error: string; loading: boolean }>();
  const sequence = useRef(0);
  const current = useRef(identity);
  current.current = identity;
  const preview = state?.identity === identity ? state.preview : null;
  const refresh = useCallback(async () => {
    const request = ++sequence.current;
    if (!reconId) {
      return;
    }
    setState(previous => ({ identity, preview: previous?.identity === identity ? previous.preview : null, error: '', loading: true }));
    try {
      const result = await getBankerReconVolumeBonusPreview(reconId, recipientId, purchaseDate);
      if (request === sequence.current && current.current === identity) {
        setState({ identity, preview: result, error: '', loading: false });
      }
    } catch {
      if (request === sequence.current && current.current === identity) {
        setState({ identity, preview: null, error: getVolumeBonusBackendStatus() === 'installed'
          ? 'Volume bonus could not be verified. Refresh before saving or finalizing.' : '', loading: false });
      }
    }
  }, [identity, reconId, recipientId, purchaseDate]);
  useEffect(() => {
    const requests = sequence;
    void refresh();
    const update = () => {
      void refresh();
    };
    window.addEventListener('focus', update);
    window.addEventListener(RECAPTURE_CHANGED_EVENT, update);
    return () => {
      requests.current++;
      window.removeEventListener('focus', update);
      window.removeEventListener(RECAPTURE_CHANGED_EVENT, update);
    };
  }, [refresh]);

  const reviewedForSave = useCallback(async (savedReconId: string) => {
    const nextIdentity = JSON.stringify([savedReconId, recipientId, purchaseDate]);
    // The first save assigns this draft its ID while the request is in flight.
    // That expected transition is safe; switching an existing loan, LO or date is not.
    const stillCurrent = () => current.current === identity || (!reconId && current.current === nextIdentity);
    let result: VolumeBonusReconPreview | null;
    try {
      result = await getBankerReconVolumeBonusPreview(savedReconId, recipientId, purchaseDate);
    } catch {
      if (!stillCurrent()) {
        throw new Error('The loan or officer changed during volume bonus review. Review and save again.');
      }
      sequence.current++;
      const error = getVolumeBonusBackendStatus() === 'installed'
        ? 'Volume bonus could not be verified. Refresh before saving or finalizing.' : '';
      setState({ identity: nextIdentity, preview: null, error, loading: false });
      if (error) {
        throw new Error(error);
      }
      return null;
    }
    if (!stillCurrent()) {
      throw new Error('The loan or officer changed during volume bonus review. Review and save again.');
    }
    sequence.current++;
    setState({ identity: nextIdentity, preview: result, error: '', loading: false });
    if (result?.applies && result.review_token !== preview?.review_token) {
      throw new Error('The volume bonus installment needs review. Its current amount is now shown under Compensation; review it and save again before finalizing.');
    }
    if (!result?.applies && preview?.applies) {
      throw new Error('This loan no longer qualifies for the displayed volume bonus. Review the refreshed compensation totals and save again.');
    }
    return result;
  }, [identity, reconId, recipientId, purchaseDate, preview]);

  return {
    preview,
    loading: getVolumeBonusBackendStatus() === 'installed' && !!reconId && (state?.identity !== identity || state.loading),
    error: state?.identity === identity ? state.error : '',
    refresh,
    reviewedForSave
  };
}
